import { Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { TipoTercero } from '@src/app/core/enum/tipo-tercero.enum';
import { initProfilingCookie, isProfilingEnabled } from '@src/app/core/utils/feature-flags.util';
import { distinctUntilChanged, filter, first } from 'rxjs/operators';

import { TranslateModule, TranslateService } from '@ngx-translate/core'; // <--- standalone only
import { AuthEventsService } from '@src/app/core/auth/auth-events.service';
import {
  ChatbotService,
  IAStatusResponse,
} from '@src/app/core/services/chatbot/chatbot.service';
import { ChatbotComponent } from '@src/app/shared/components/chatbot/chatbot.component';
import { LoadingOverlayComponent } from '@src/app/shared/components/loading-overlay/loading-overlay.component';
import { PrimeNG } from 'primeng/config';
import { ToastModule } from 'primeng/toast';
import { AuthSelectors } from './store/auth/auth.selectors';
import { GlobalCacheConfig } from 'ts-cacheable';

declare var gtag: Function;
@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    TranslateModule,
    ToastModule,
    LoadingOverlayComponent,
    ChatbotComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  store = inject(Store);
  title = 'EcoembesTemplate';
  user = this.store.selectSignal(AuthSelectors.selectAuthUser);

  chatbotEnabledByBackend = signal<boolean>(false);
  chatbotVisibleByRoute = signal<boolean>(false);

  enabledChatbot = computed(
    () => this.chatbotEnabledByBackend() && this.chatbotVisibleByRoute(),
  );

  constructor(
    private translate: TranslateService,
    private router: Router,
    private config: PrimeNG,
    private chatbotService: ChatbotService,
    private authEvents: AuthEventsService,
  ) {
    // PBI-866415 / PBI-877361: escuchar los eventos OIDC (SilentRenewFailed) desde el arranque.
    this.authEvents.init();

    this.translate.addLangs(['es', 'en', 'eu', 'ca', 'gl']);
    this.translate.setDefaultLang('es');
    this.translate.use('es');
    this.translate
      .get('primeng')
      .subscribe((res) => this.config.setTranslation(res));
  }

  private scrollToTopOnNavigationAndAnalytics() {
    this.router.events.subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);

      // No mostrar el chat en la pantalla de login
      // Solo controla visibilidad por ruta
      this.chatbotVisibleByRoute.set(evt.urlAfterRedirects !== '/login');

      try {
        if (typeof gtag === 'function') {
          gtag('config', 'G-MJHSYYNDS5', {
            page_path: evt.urlAfterRedirects,
          });
        }
      } catch (e) {
        console.warn('Error sending pageview event to GA4', e);
      }
    });
  }

  /**
   * Inicializa el estado de activación de IA del chatbot.
   * Solo se ejecuta la primera vez que se navega fuera del login con usuario autenticado.
   */
  private initializeAIActivationStatus() {
    GlobalCacheConfig.maxAge = 5 * 60 * 1000; // 5 minutos

    this.router.events
      .pipe(
        filter((evt): evt is NavigationEnd => evt instanceof NavigationEnd),
        filter(
          (evt) =>
            evt.urlAfterRedirects !== '/login' && !!this.user()?.NOMBRE_DS,
        ),
        first(),
      )
      .subscribe({
        next: () => {
          this.chatbotService.getAIActivationStatus().subscribe({
            next: (resp: IAStatusResponse) => {
              this.chatbotEnabledByBackend.set(resp.chatbot_enabled);
            },
            error: (err) => {
              this.chatbotEnabledByBackend.set(false);
              console.error('Error al obtener estado de IA:', err);
            },
          });
        },
      });
  }

  ngOnInit() {
    initProfilingCookie();
    initProfilingCookie();
    this.scrollToTopOnNavigationAndAnalytics();
    this.initializeAIActivationStatus();
    this.enforceProfilingAccess();
    this.watchCompanyProfile();
  }

  /**
   * Diagnóstico: registra en consola el perfil de la empresa seleccionada y el estado
   * del feature flag VIEW_PF cada vez que cambia la compañía activa.
   */
  private watchCompanyProfile(): void {
    this.store
      .select(AuthSelectors.selectSelectedCompany)
      .pipe(
        filter((company) => !!company?.TIPO_TERCERO),
        distinctUntilChanged(
          (a, b) =>
            a?.TIPO_TERCERO === b?.TIPO_TERCERO && a?.POSEE_ID === b?.POSEE_ID,
        ),
      )
      .subscribe((company) => {
        console.log(
          '[PF] Company profile:',
          company?.TIPO_TERCERO,
          '| POSEE_ID:',
          company?.POSEE_ID ?? 'n/a',
        );

        const isPFI =
          company?.TIPO_TERCERO === TipoTercero.POSEEDOR_FINAL_INTEGRAL ||
          (company?.TIPO_TERCERO === TipoTercero.TERCEROS_EMPRESAS_ADHERIDAS &&
            company?.POSEE_ID === 'PI');

        if (isPFI) {
          if (isProfilingEnabled()) {
            console.log('[PF] perfil PFI habilitado por Cookies');
          } else {
            console.log('[PF] perfil de PFI bloqueado por cookies');
          }
        }
      });
  }

  /**
   * Cuando VIEW_PF=false, bloquea el acceso a usuarios cuyo TIPO_TERCERO no es TERCEROS_EMPRESAS_ADHERIDAS.
   * Espera a que selectedCompany esté disponible en el store antes de evaluar.
   */
  private enforceProfilingAccess(): void {
    if (isProfilingEnabled()) return;

    this.store
      .select(AuthSelectors.selectSelectedCompany)
      .pipe(
        filter((company) => !!company?.TIPO_TERCERO),
        first(),
      )
      .subscribe((company) => {
        if (company!.TIPO_TERCERO !== TipoTercero.TERCEROS_EMPRESAS_ADHERIDAS) {
          this.router.navigate(['/forbidden']);
        }
      });
  }
}
